/**
 * Module dependencies.
 */

import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import React, { ForwardedRef, forwardRef } from 'react';
import { CardSelect, CardSelectProps } from './card-select';

/**
 * `CardSelectFieldProps` type.
 */

export type CardSelectFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<CardSelectProps, 'name' | 'onChange' | 'value'> & {
    label: string;
  };

/**
 * `CardSelectFieldComponent` component.
 */

export function CardSelectFieldComponent<T extends FieldValues>(
  props: CardSelectFieldProps<T>,
  forwardedRef: ForwardedRef<any>
): JSX.Element {
  const { control, defaultValue, name, rules, shouldUnregister, ...inputProps } = props;

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { name, onBlur, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => {
        return (
          <CardSelect
            invalid={invalid}
            isDirty={isDirty}
            isTouched={isTouched}
            name={name}
            onBlur={onBlur}
            value={value}
            {...inputProps}
            error={error}
            onChange={newValue => {
              onChange(!newValue && newValue !== 0 ? undefined : newValue);
            }}
            ref={forwardedRef}
          />
        );
      }}
      rules={rules}
      shouldUnregister={shouldUnregister}
    />
  );
}

/**
 * Export `CardSelectField`.
 */

export const CardSelectField = forwardRef<CardSelectFieldProps<any>, CardSelectFieldProps<any>>(
  CardSelectFieldComponent
);
